import { NumberFormatValues } from 'react-number-format';
import {
  LIMITS,
  COMPANY_TYPES,
  FOR_PROFIT_COMPANY_TYPES,
  BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT,
  INDUSTRY_BORROWER_REPORTED_TYPES,
  NON_PROFIT_ORGANIZATION_TYPES,
  PG_FOR_PROFIT_COMPANY_TYPES,
  PG_COMPANY_TYPES,
  INDUSTRY_PG_TYPES,
  FEATURE_FLAGS_MULTI,
  EnumFeatureType,
  MILESTONE_SHORT_TO_LONG,
  MilestoneLabel,
  RELATED_PARTY_RELATIONSHIP,
  RELATED_PARTY_TYPES,
} from '../../../constants';
import {
  Application,
  DocumentUserGroup,
  LoanCategory,
  Milestone,
  ProductFamily,
  ProductFamilyLoanTypeMapping,
  RequiredDocument,
} from '../../../../../../types/api';
import { zonedTimeToUtc } from 'date-fns-tz';
import { api } from '../../../services/apis';
import { ApplicationFormValues } from '../../../pages/dashboard/loan_detail/LoanDetailTypes';
import { APPLICATION_FLOW_TYPE } from '../../../pages/dashboard/loan_detail/flows/constants';
import { RulesetEngine } from '../../../pages/dashboard/loan_detail/flows/NewWelcomeFlow/business/ruleset_engine/RulesetEngine';
import {
  MilestonePaymentsInput,
  MilestonePaymentsResult,
} from '../../../pages/pricing-calculator/types';

const forProfitCompanyIsPGOrEmpty = (forProfitCompanyType) => {
  return (
    PG_FOR_PROFIT_COMPANY_TYPES.includes(forProfitCompanyType) ||
    forProfitCompanyType === ''
  );
};

// TODO: We can remove this once we have no legacy applications
const companyTypeIsPGOrEmpty = (companyType) => {
  return PG_COMPANY_TYPES.includes(companyType) || companyType === '';
};

// TODO: We can remove this Company Type mapping once we have no legacy applications
const setBusinessTypeForProfitOrNonProfit = (fv: ApplicationFormValues) => {
  const companyType = fv.solar_system_power_company_type as COMPANY_TYPES;
  switch (companyType) {
    case COMPANY_TYPES.OPERATING_COMPANY:
      fv.business_type_for_profit_or_non_profit =
        BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.FOR_PROFIT;
      fv.for_profit_company_type =
        FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES;
      break;
    case COMPANY_TYPES.PROPERTY_COMPANY:
      fv.business_type_for_profit_or_non_profit =
        BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.FOR_PROFIT;
      fv.for_profit_company_type =
        FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS;
      break;
    case COMPANY_TYPES.AGRICULTURE:
      fv.business_type_for_profit_or_non_profit =
        BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.FOR_PROFIT;
      // Since we don't have an equivalent for Agriculture, either sell goods or services or collect rent from tenants will work
      fv.for_profit_company_type =
        FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES;
      fv.industry_borrower_reported =
        INDUSTRY_BORROWER_REPORTED_TYPES.AGRICULTURE;
      break;
    case COMPANY_TYPES.NON_PROFIT:
      fv.business_type_for_profit_or_non_profit =
        BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.NON_PROFIT;
      fv.industry_borrower_reported =
        INDUSTRY_BORROWER_REPORTED_TYPES.NON_PROFIT;
      if (fv.is_religious_organization === 'Yes') {
        fv.non_profit_organization_type = NON_PROFIT_ORGANIZATION_TYPES.FBO;
      } else {
        fv.non_profit_organization_type = NON_PROFIT_ORGANIZATION_TYPES.OTHER;
      }
      break;
    case COMPANY_TYPES.HOA:
      fv.business_type_for_profit_or_non_profit =
        BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.NON_PROFIT;
      fv.non_profit_organization_type = NON_PROFIT_ORGANIZATION_TYPES.HOA;
      fv.industry_borrower_reported =
        INDUSTRY_BORROWER_REPORTED_TYPES.NON_PROFIT;
      break;
  }
};

function getNonProfitCategory(
  type: NON_PROFIT_ORGANIZATION_TYPES
): LoanCategory {
  switch (type) {
    case NON_PROFIT_ORGANIZATION_TYPES.FBO:
      return LoanCategory.NPO_FBO;
    case NON_PROFIT_ORGANIZATION_TYPES.HOA:
      return LoanCategory.NPO_HOA;
    case NON_PROFIT_ORGANIZATION_TYPES.EDU:
      return LoanCategory.NPO_EDUCATIONAL_INSTITUTE;
    default:
      return LoanCategory.NPO_OTHER;
  }
}

function getIndustryPGCategory(
  type: FOR_PROFIT_COMPANY_TYPES
): LoanCategory | undefined {
  switch (type) {
    case FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES:
      return LoanCategory.INDUSTRY_PG_OPCO;
    case FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS:
      return LoanCategory.INDUSTRY_PG_PROPCO;
  }
}

function getCoreCategory(
  type: FOR_PROFIT_COMPANY_TYPES
): LoanCategory | undefined {
  switch (type) {
    case FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES:
      return LoanCategory.CORE_OPCO;
    case FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS:
      return LoanCategory.CORE_PROPCO;
  }
}

function getTieredCategory(
  type: FOR_PROFIT_COMPANY_TYPES,
  loanAmountFlags: {
    isExpressLoanAmount: boolean;
    isExpressPlusLoanAmount: boolean;
  }
): LoanCategory | undefined {
  const { isExpressLoanAmount, isExpressPlusLoanAmount } = loanAmountFlags;

  switch (type) {
    case FOR_PROFIT_COMPANY_TYPES.SELLS_GOODS_OR_SERVICES:
      if (isExpressLoanAmount) return LoanCategory.EXPRESS_OPCO;
      if (isExpressPlusLoanAmount) return LoanCategory.EXPRESS_PLUS_OPCO;
      return LoanCategory.CORE_OPCO;

    case FOR_PROFIT_COMPANY_TYPES.COLLECTS_RENT_FROM_TENANTS:
      if (isExpressLoanAmount) return LoanCategory.EXPRESS_PROPCO;
      if (isExpressPlusLoanAmount) return LoanCategory.EXPRESS_PLUS_PROPCO;
      return LoanCategory.CORE_PROPCO;
  }
}

const getForProfitCategory = (
  forProfitCompanyType: FOR_PROFIT_COMPANY_TYPES,
  loanAmountFlags: {
    isPPALoanAmount: boolean;
    isIndustryPGLoanAmount: boolean;
    isIndustryPG: boolean;
    isExpressLoanAmount: boolean;
    isExpressPlusLoanAmount: boolean;
  }
) => {
  const {
    isPPALoanAmount,
    isIndustryPGLoanAmount,
    isIndustryPG,
    isExpressLoanAmount,
    isExpressPlusLoanAmount,
  } = loanAmountFlags;

  // PPA Archetype (Sell Solar-Generated Power to Third Parties option only shows if loan amount is above PPA floor)
  if (
    isPPALoanAmount &&
    forProfitCompanyType ===
      FOR_PROFIT_COMPANY_TYPES.SELLS_SOLAR_GENERATED_POWER_TO_THIRD_PARTIES
  ) {
    return LoanCategory.PPA_FINANCE;
  }

  // Industry PG Archetypes (Industries that require a PG)
  if (isIndustryPG && isIndustryPGLoanAmount) {
    return getIndustryPGCategory(forProfitCompanyType);
  }

  // If loan amount is large but industry is NOT in Industry PG list, fallback to Core Archetype
  if (isIndustryPGLoanAmount && !isIndustryPG) {
    return getCoreCategory(forProfitCompanyType);
  }

  // Normal (non-industry) categorization (Express, Express Plus, Core Archetypes)
  return getTieredCategory(forProfitCompanyType, {
    isExpressLoanAmount,
    isExpressPlusLoanAmount,
  });
};

export const OPTIONAL_PG_CATEGORIES: LoanCategory[] = [
  LoanCategory.EXPRESS_OPCO,
  LoanCategory.EXPRESS_PROPCO,
  LoanCategory.EXPRESS_PLUS_OPCO,
  LoanCategory.EXPRESS_PLUS_PROPCO,
  LoanCategory.INDUSTRY_PG_OPCO,
  LoanCategory.INDUSTRY_PG_PROPCO,
];

const allPrincipalsDeclinedPersonalGuarantee = (
  principals: ApplicationFormValues['principals']
): boolean => {
  const ownerPrincipals =
    principals?.filter(
      (principal) =>
        principal.relationship === RELATED_PARTY_RELATIONSHIP.PRINCIPAL
    ) ?? [];

  return (
    ownerPrincipals.length > 0 &&
    ownerPrincipals.every(
      (principal) => principal.principal_is_personal_guarantor === 'No'
    )
  );
};

export const isOptionalPgNaturalCategory = (
  category?: LoanCategory | null
): boolean => {
  return (
    category === LoanCategory.CORE_PROPCO ||
    (!!category && OPTIONAL_PG_CATEGORIES.includes(category))
  );
};

const isPGRequired = (app: ApplicationFormValues) => {
  /**
   * This function aims to decouple the logic for when a Loan App requires a PG ('Is [PRINCIPAL] willing to be a Personal Guarantor?'),
   * from the 'requiresPGPercentOwnership()' function.
   * allowing it to scale easier and keep adding rules as the business logic changes.
   * This is not related to the principalIsPG field validation, but to the section completion,
   * determining when it can be completed without providing a PG
   */
  try {
    return RulesetEngine.evaluate(
      EnumFeatureType.PG,
      app as ApplicationFormValues
    );
  } catch (err) {
    console.error(
      '[isPGRequired] - RulesetEngine failed → defaulting PG required to False',
      err
    );
    return false;
  }
};

export const getNaturalLoanCategory = (
  fv: ApplicationFormValues
): LoanCategory | undefined => {
  const {
    loan_amount,
    for_profit_company_type,
    business_type_for_profit_or_non_profit,
    non_profit_organization_type,
    industry_borrower_reported,
  } = fv;

  // Checks:
  const isExpressLoanAmount =
    loan_amount && loan_amount <= LIMITS.PG_LOAN_AMOUNT_CEILING;

  const isExpressPlusLoanAmount =
    loan_amount &&
    loan_amount > LIMITS.EXPRESS_PLUS_LOAN_AMOUNT_FLOOR &&
    loan_amount <= LIMITS.EXPRESS_PLUS_LOAN_AMOUNT_CEILING;

  const isPPALoanAmount =
    loan_amount && loan_amount > LIMITS.PPA_LOAN_AMOUNT_FLOOR;

  const isIndustryPGLoanAmount =
    loan_amount && loan_amount > LIMITS.PG_INDUSTRY_LOAN_AMOUNT_FLOOR;

  const isIndustryPG = INDUSTRY_PG_TYPES.includes(
    industry_borrower_reported as INDUSTRY_BORROWER_REPORTED_TYPES
  );

  // If necessary values are not set, return undefined
  if (
    !business_type_for_profit_or_non_profit ||
    (!for_profit_company_type && !non_profit_organization_type)
  ) {
    return undefined;
  }

  // Non-Profit Archetypes
  if (
    business_type_for_profit_or_non_profit ===
    BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.NON_PROFIT
  ) {
    return getNonProfitCategory(
      non_profit_organization_type as NON_PROFIT_ORGANIZATION_TYPES
    );
  }

  // For-Profit Archetypes
  if (
    business_type_for_profit_or_non_profit ===
    BUSINESS_TYPE_FOR_PROFIT_OR_NON_PROFIT.FOR_PROFIT
  ) {
    return getForProfitCategory(
      for_profit_company_type as FOR_PROFIT_COMPANY_TYPES,
      {
        isPPALoanAmount: isPPALoanAmount || false,
        isIndustryPGLoanAmount: isIndustryPGLoanAmount || false,
        isIndustryPG: isIndustryPG || false,
        isExpressLoanAmount: isExpressLoanAmount || false,
        isExpressPlusLoanAmount: isExpressPlusLoanAmount || false,
      }
    );
  }
};

export const determineArchetype = (
  fv: ApplicationFormValues,
  flow: APPLICATION_FLOW_TYPE
) => {
  // TODO: We can remove this once we have no legacy applications, as only old applications can't set those values in the form
  // This sets the business type for profit or non-profit for old applications for the switch below to work
  if (flow === APPLICATION_FLOW_TYPE.MAIN) {
    setBusinessTypeForProfitOrNonProfit(fv);
  }

  const naturalCategory = getNaturalLoanCategory(fv);

  const pgRequired = isPGRequired({
    ...fv,
    loan_category: naturalCategory,
  });

  if (
    !pgRequired &&
    allPrincipalsDeclinedPersonalGuarantee(fv.principals) &&
    naturalCategory &&
    OPTIONAL_PG_CATEGORIES.includes(naturalCategory)
  ) {
    return getCoreCategory(
      fv.for_profit_company_type as FOR_PROFIT_COMPANY_TYPES
    );
  }

  return naturalCategory;
};

export const updateLoanCategory = (control, flow: APPLICATION_FLOW_TYPE) => {
  const fv = control._formValues;
  fv.loan_category = determineArchetype(fv, flow);
};

const shouldRPBeIncludedOnMinimumPGOwnershipCalculation = (
  principal
): boolean => {
  if (principal?.principal_is_personal_guarantor === 'Yes') return true;
  return (
    principal?.principal_type === RELATED_PARTY_TYPES.ENTITY &&
    principal?.relationship === RELATED_PARTY_RELATIONSHIP.PRINCIPAL
  );
};

export const calculatePGPercentOwnership = (principals): number => {
  // only relevant for PG loans
  let pgPercentOwnership = 0;
  principals = principals || [];
  for (const principal of principals) {
    if (
      shouldRPBeIncludedOnMinimumPGOwnershipCalculation(principal) &&
      principal.percent_ownership
    ) {
      pgPercentOwnership += parseFloat(principal.percent_ownership);
    }
  }
  return parseFloat(pgPercentOwnership.toFixed(2));
};

export const countPGs = (principals): number => {
  return principals.filter(shouldRPBeIncludedOnMinimumPGOwnershipCalculation)
    .length;
};

export const isValidSFCurrency = (values: NumberFormatValues): boolean => {
  const { floatValue } = values;
  if (floatValue) return floatValue >= 0 && floatValue <= 10000000000000000;
  return true;
};

export const formatPhoneNumber = (phoneNumber) => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return null;
};

export const shortenFilename = (name: string, maxLength: number) => {
  if (name.length <= maxLength) {
    return name;
  }
  return name.substring(0, maxLength - 3) + '...';
};

export const requiresPGPercentOwnership = (application: Application) => {
  // If the app definitely needs a PG, we must ensure the “percent ownership”
  // question is answered; otherwise we can let the section pass.
  return isPGRequired(application as unknown as ApplicationFormValues);
};

export const missingApplicantSameAsPrincipal = (application: Application) => {
  /* This utility function is to be used in ApplicationStepsContext and ApplicationPage, since it overwrites the section
  validation, to make the section incomplete if missing the field Applicant same as Principal 
  -which is already required in Yup validation- */
  if (!application.principals || application.principals.length === 0) {
    return false;
  }

  const hasApplicantSameAsPrincipal = application.principals.some(
    (principal) => principal.applicant_same_as_principal === 'Yes'
  );

  // We need to check:
  /**
   - Pricipal selected Yes to is PG
   - Didn't select any field for ASAP
   - AND there's no other Principal that already selected 'Yes' to ASAP (if so, that question will never show and will always be null)
   */
  const missingAsap = application.principals.some(
    (principal) =>
      principal.principal_is_personal_guarantor === 'Yes' &&
      principal.applicant_same_as_principal === null &&
      !hasApplicantSameAsPrincipal
  );

  return missingAsap;
};

export const isYes = (value: 'Yes' | 'No' | null | undefined): boolean =>
  value === 'Yes';
export const isNo = (value: 'Yes' | 'No' | null | undefined): boolean =>
  value === 'No';
export const isCurrentDateBetween = (startDate, endDate) => {
  const currentTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const currentDateUTC = zonedTimeToUtc(new Date(), currentTz);

  const referenceTz = 'America/New_York';
  const startDateUTC = zonedTimeToUtc(startDate, referenceTz);
  const endDateUTC = zonedTimeToUtc(endDate, referenceTz);
  return currentDateUTC >= startDateUTC && currentDateUTC <= endDateUTC;
};

interface SelectedProduct {
  rate: number;
  amortizationYears: number;
  loanTermYears: number;
  productFamily: ProductFamily;
}

interface SendMonthlyPaymentRequestParams {
  selectedProduct: SelectedProduct;
  loanAmount: number;
  specialPaymentPercentage?: number;
}

export const sendMonthlyPaymentRequest = async ({
  selectedProduct,
  loanAmount,
  specialPaymentPercentage,
}: SendMonthlyPaymentRequestParams) => {
  // Send a request to Railway endpoint to calculate the monthly payment

  const { rate, amortizationYears, loanTermYears, productFamily } =
    selectedProduct;

  const prodFamilyShort: string = ProductFamilyLoanTypeMapping[productFamily];

  const params = {
    loanAmount: loanAmount.toString(),
    annualRate: rate.toString(),
    numberOfYears: loanTermYears.toString(),
    type: prodFamilyShort,
    amortizationYears: amortizationYears.toString(),
    ...(specialPaymentPercentage
      ? { specialPaymentPercentage: specialPaymentPercentage.toString() }
      : {}),
  };

  const queryString = new URLSearchParams(params).toString();
  const response = await api().get(`calculate-monthly-payment?${queryString}`);

  const data = JSON.parse(response.data.join(''));

  return data.monthlyPayment;
};

/**
 * Returns array of the last four years including the current year
 */
export const getTaxYears = (years = 4): number[] => {
  const currentYear = new Date().getFullYear();
  return Array(years + 1)
    .fill(0)
    .map((_, i) => currentYear - i);
};

export const getTaxYearsStr = (): string[] => {
  return getTaxYears().map((year) => year.toString());
};

// adjusting for tax season in april.  from Jan - April, this will be last year, after April will be the current year
export const getPG1Year = (): number => {
  const currentYear = new Date().getFullYear();
  return currentYear - (new Date().getMonth() + 1 < 5 ? 2 : 1);
};

export const powerSoldToSingleUserCondition = (
  singleOrMultipleUsers: Application['system_power_single_or_multiple_users']
) => {
  return singleOrMultipleUsers === 'Single User';
};

export const powerIsCommunitySolarCondition = (
  singleOrMultipleUsers: Application['system_power_single_or_multiple_users'],
  communityOrPPALease: Application['community_solar_or_ppa_lease']
) => {
  return (
    singleOrMultipleUsers === 'Multiple Users' &&
    communityOrPPALease === 'Community Solar Arrangement'
  );
};

export const equalsValueOrIsNullUndefined = (field: any, value: any) => {
  return [null, undefined, value].includes(field);
};

export const docHasNoInternalPermissions = (doc: RequiredDocument) => {
  return (
    doc.can_view !== DocumentUserGroup.INTERNAL &&
    doc.can_upload !== DocumentUserGroup.INTERNAL
  );
};

export const isFeatureFlagEnabled = (
  field: string,
  featureFlag: FEATURE_FLAGS_MULTI
) => {
  const featureFlags = field.split(';') || [];
  return featureFlags.includes(featureFlag);
};

interface SendMilestonePaymentsRequestParams {
  loanAmount: number;
  dealerFee: number;
  borrowerAdvanceRate: number;
  milestones: MilestonePaymentsInput[];
  roundToCents?: boolean;
}

export const sendMilestonePaymentsRequest = async ({
  loanAmount,
  dealerFee,
  borrowerAdvanceRate,
  milestones,
  roundToCents,
}: SendMilestonePaymentsRequestParams): Promise<MilestonePaymentsResult> => {
  const response = await api().post('calculate-milestone-payments', {
    loanAmount,
    dealerFee,
    borrowerAdvanceRate,
    milestones,
    roundToCents,
  });

  return response.data;
};

export const fetchBusinessConstants = async () => {
  const response = await api().get('business-constants');
  return response.data;
};

export interface MilestoneDefaultPaymentPctItem {
  type: string;
  title: string;
  defaultPaymentPct: number;
}

export const fetchMilestoneDefaultPaymentPcts = async (
  programName: string
): Promise<MilestoneDefaultPaymentPctItem[]> => {
  const response = await api().get('milestone-default-payment-pcts', {
    params: { program_name: programName },
  });
  return response.data;
};

export type MilestoneDrawConfigItem = {
  milestone: MilestoneLabel;
  maxDrawPercentage: number;
  milestoneType: Milestone['type'];
};

/**
 * Transform API default payment pcts to the shape expected by MilestoneCostCalculator and validation.
 * Only includes types that exist in MILESTONE_SHORT_TO_LONG.
 */
export const transformMilestoneDefaultPctsToDrawConfig = (
  items: MilestoneDefaultPaymentPctItem[]
): MilestoneDrawConfigItem[] => {
  return items
    .filter((item) => item.type in MILESTONE_SHORT_TO_LONG)
    .map((item) => {
      const milestone =
        MILESTONE_SHORT_TO_LONG[
          item.type as keyof typeof MILESTONE_SHORT_TO_LONG
        ];
      return {
        milestone,
        maxDrawPercentage: item.defaultPaymentPct / 100,
        milestoneType: item.type as Milestone['type'],
      };
    });
};
